import React, { useState, useEffect } from 'react';
import { Grid, TextField } from '@mui/material';
import { Alert, DatePicker, SearchField } from '../../../components';
import { useAdvancedUserSearch } from './graphql/useAdvancedUserSearch';
import dayjs from 'dayjs';

export type StaffProps = {
  prefix: string;
  staffData: any;
  onStaffChange: (newData: any) => void;
};

export const Staff: React.FunctionComponent<StaffProps> = ({
  staffData,
  onStaffChange,
}) => {
  const [searchNetId, setSearchNetId] = useState<string>('');
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertDetails, setAlertDetails] = useState<any>({
    alertContent: '',
    alertType: 'error',
  });
  const [searchTriggered, setSearchTriggered] = useState<boolean>(false);
  const [localStaffData, setLocalStaffData] = useState<any>(staffData);

  // Use the custom hook to search for a user based on NetID, but skip the query unless searchTriggered is true
  const { data, error, loading } = useAdvancedUserSearch(
    searchNetId,
    !searchTriggered,
  );

  // Update local staff data and propagate changes to the parent when the staffData prop changes
  useEffect(() => {
    setLocalStaffData(staffData);
  }, [staffData]);

  // Handle search trigger
  const handleSearch = (): void => {
    setSearchTriggered(true); // Trigger the search
  };

  // Update local staff data when search result is returned
  useEffect(() => {
    if (searchTriggered && !loading) {
      if (data?.advancedUser && data?.advancedUser != null) {
        const updatedStaff = {
          ...localStaffData,
          netId: data.advancedUser.netId,
          email: data.advancedUser.email,
          surname: data.advancedUser.surname,
          givenName: data.advancedUser.givenName,
          title: data.advancedUser.title,
          department: data.advancedUser.department,
        };
        setLocalStaffData(updatedStaff);
        onStaffChange(updatedStaff); // Notify parent component of the change
        setSearchTriggered(false); // Reset the trigger
      } else {
        setAlertDetails({
          alertContent: `No user found for NetID ${searchNetId}`,
          alertType: 'warning',
        });
        setAlertOpen(true);
        setSearchTriggered(false); // Reset the trigger
      }
    }
  }, [
    data,
    error,
    searchTriggered,
    searchNetId,
    onStaffChange,
    localStaffData,
    loading,
  ]);

  const handleChange = (key: string, value: any): void => {
    const updatedData = { ...localStaffData, [key]: value };
    setLocalStaffData(updatedData);
    onStaffChange(updatedData); // Notify parent component of the change
  };

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <SearchField
          content={searchNetId}
          placeholder="Search NetID"
          handleContentChange={e => {
            setSearchNetId(e.target.value);
          }}
          handleSearch={handleSearch}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
        }}
      >
        <TextField
          fullWidth
          label="NetID"
          name="netId"
          value={localStaffData.netId ?? ''}
          disabled
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
        }}
      >
        <TextField
          fullWidth
          label="Email"
          name="email"
          value={localStaffData.email ?? ''}
          onChange={e => {
            handleChange('email', e.target.value);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
        }}
      >
        <TextField
          fullWidth
          label="Surname"
          name="surname"
          value={localStaffData.surname ?? ''}
          onChange={e => {
            handleChange('surname', e.target.value);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
        }}
      >
        <TextField
          fullWidth
          label="Given Name"
          name="givenName"
          value={localStaffData.givenName ?? ''}
          onChange={e => {
            handleChange('givenName', e.target.value);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
        }}
      >
        <TextField
          fullWidth
          label="Title"
          name="title"
          value={localStaffData.title ?? ''}
          onChange={e => {
            handleChange('title', e.target.value);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
        }}
      >
        <TextField
          fullWidth
          label="Department"
          name="department"
          value={localStaffData.department ?? ''}
          onChange={e => {
            handleChange('department', e.target.value);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
        }}
      >
        <DatePicker
          label="Effective From"
          name="effectiveFrom"
          value={
            localStaffData.effectiveFrom !== null
              ? dayjs(localStaffData.effectiveFrom)
              : null
          }
          onChange={date => {
            handleChange('effectiveFrom', date);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
        }}
      >
        <DatePicker
          label="Effective To"
          name="effectiveTo"
          value={
            localStaffData.effectiveTo !== null
              ? dayjs(localStaffData.effectiveTo)
              : null
          }
          onChange={date => {
            handleChange('effectiveTo', date);
          }}
        />
      </Grid>
      <Alert
        openAlert={alertOpen}
        setOpenAlert={setAlertOpen}
        alertDetails={alertDetails}
      />
    </Grid>
  );
};
